import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Email asks placed where they help the shopper (snippets/mlb-email-ask.liquid), instead of a pop-up:
 * "Email me my bag" under the bag's checkout button, and "Email me if it's restocked" on product pages
 * while the chosen size is sold out.
 *
 * Each is a Shopify customer (newsletter) form. It is sent in the background so the shopper stays where they are,
 * after Shopify's own spam check (hCaptcha) has run; when Shopify answers with a full captcha page instead, the form
 * posts normally so the shopper can complete it.
 * The bag ask's tags carry the bag (bag:<yymmddhhmmss UTC>:<variant>x<qty>.…): the snippet renders them from the cart
 * and the send refreshes them from /cart.js. The saved-bag email (Flow "Email saved bag", Messaging) rebuilds a link
 * to the bag from the newest such tag.
 */
const PENDING = 'data-mlb-size-pending';
const SIZE_FIELDSET = ':is(.variant-option--size-system, .variant-option--hat-size)';
// Shopify customer tags are limited to 255 characters
const MAX_TAG_LENGTH = 255;
// Shopify's check attaches on the form's first focus; on a slow connection its script takes a few seconds to load
const CHECK_ATTACH_WAIT = 8000;
// Unseen, the check answers within a second or two; a puzzle the shopper has to solve can take a while
const CHECK_TIMEOUT = 20000;
const PUZZLE_TIMEOUT = 180000;
// A puzzle that closes with no send following was closed unsolved
const PUZZLE_CLOSED_GRACE = 2000;

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} tags
 * @property {HTMLInputElement} email
 * @property {HTMLElement} panel
 * @property {HTMLElement} done
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} [opener]
 */

/**
 * @typedef {HTMLElement & {isOpen?: boolean, open?: () => void, close?: () => void}} Drawer
 */

/** @extends {Component<Refs>} */
class MlbEmailAsk extends Component {
  requiredRefs = ['tags', 'email', 'panel', 'done', 'error'];

  #abortController = new AbortController();
  /** The post to Shopify is in flight */
  #busy = false;
  #retried = false;
  #waiting = false;
  #watching = false;
  /** @type {Drawer | null} the bag, closed while a captcha puzzle is up */
  #reopen = null;
  /** Restock: the sizes already asked about */
  #doneVariants = new Set();
  /** @type {string | undefined} Restock: the size shown when the shopper pressed the button */
  #sentVariant;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    // Listeners on the element, not on:focusin / on:submit (document listeners): they must run before the
    // captcha's own document listeners (#onFocusIn)
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    if (this.dataset.kind === 'restock') this.#followSizes(signal);
  }

  updatedCallback() {
    super.updatedCallback();
    // A re-render restores the markup as Liquid rendered it
    if (this.dataset.kind === 'restock') this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** Bag: "Not ready yet? Email me my bag" opens the form */
  open() {
    const { opener, panel, email } = this.refs;
    opener?.setAttribute('aria-expanded', 'true');
    if (opener) opener.hidden = true;
    panel.hidden = false;
    email.focus();
  }

  /**
   * Restock: follow the size picker. Shown while the chosen size is sold out, or straight away when every
   * size is; hidden while no size has been chosen yet (mlb-size-required.js), since "your size" means nothing then.
   * @param {AbortSignal} signal
   */
  #followSizes(signal) {
    const root = this.closest('.shopify-section, dialog');
    if (root) {
      root.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
      const observer = new MutationObserver(() => this.#update());
      observer.observe(root, { attributes: true, attributeFilter: [PENDING] });
      signal.addEventListener('abort', () => observer.disconnect());
    }
    this.#update();
  }

  /** @param {ProductSelectEvent} event */
  #onProductSelect = (event) => {
    // Size pickers on product cards in the same section belong to other products
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then((result) => {
        const detail =
          /** @type {{html?: Document, newProduct?: object, productId?: string, resource?: {id: number, available: boolean}} | undefined} */ (
            result.detail
          );
        if (!detail?.newProduct && detail?.productId && String(detail.productId) !== this.dataset.productId) return;

        // The section as rendered for the new choice is the source of truth; the event's variant is the fallback
        const next = detail?.html?.querySelector('mlb-email-ask[data-kind="restock"]');
        if (next instanceof HTMLElement) {
          for (const name of ['productId', 'handle', 'variant', 'available', 'productAvailable']) {
            const value = next.dataset[name];
            if (value !== undefined) this.dataset[name] = value;
          }
        } else {
          const id = detail?.resource?.id ?? result.variant?.id.split('/').pop();
          const available = detail?.resource ? detail.resource.available : result.variant?.availableForSale;
          if (id) this.dataset.variant = String(id);
          this.dataset.available = String(available !== false);
        }
        this.#showError(false);
        this.#update();
      })
      .catch((error) => {
        // A superseded size change rejects; the next one updates the ask
        if (error?.name !== 'AbortError') console.warn('[mlb-email-ask] Size change rejected:', error);
      });
  };

  /** Restock: show the ask for the chosen size, with the form, or the thanks for a size already asked about */
  #update() {
    if (this.closest('.quick-add-modal')) {
      // Shopify's captcha puzzle can't show above the quick-add window; the product page has the ask
      this.hidden = true;
      return;
    }
    const root = this.closest('.shopify-section');
    const variant = this.dataset.variant ?? '';
    const soldOut = this.dataset.available === 'false';
    const everySizeGone = this.dataset.productAvailable === 'false' || (root ? noSizeLeft(root) : false);
    const waitingForSize = !!root?.hasAttribute(PENDING) && !everySizeGone;

    if (this.getAttribute('data-state') !== 'sending') {
      this.#showDone(this.#doneVariants.has(variant));
      this.refs.tags.value = restockTags(this.dataset.handle, variant);
    }
    this.hidden = !soldOut || (waitingForSize && !this.hasAttribute('data-state'));
  }

  /**
   * Shopify's spam protection (hCaptcha) binds to the form on its first focus: it keeps whatever form.submit is at
   * that moment, replaces it, and calls the kept one once the shopper passes the captcha. Making form.submit our
   * background send before that sends the captcha token with it. Runs before the captcha's own document listener.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const form = event.target instanceof Element ? event.target.closest('form') : null;
    if (form) this.#prepare(form);
  };

  /** @param {HTMLFormElement} form */
  #prepare(form) {
    if (form.hasAttribute('data-mlb-send')) return;
    form.setAttribute('data-mlb-send', '');
    form.submit = () => this.#send(form);
  }

  /**
   * Once the captcha is bound it handles the submit (and calls #send through form.submit). Until then, wait for it
   * to attach; on a shop without it, send.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (this.#busy) {
      event.preventDefault();
      return;
    }
    const focused = form.hasAttribute('data-mlb-send');
    this.#prepare(form);
    this.#sentVariant = this.dataset.variant;
    this.setAttribute('data-state', 'sending');
    this.#showError(false);

    if (form.dataset.hcaptchaBound) {
      this.#watchCheck();
      return;
    }
    event.preventDefault();
    this.#waitForCheck(form, focused);
  };

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} focused - whether the form was focused before (when the captcha starts attaching)
   */
  async #waitForCheck(form, focused) {
    if (this.#waiting) return;
    this.#waiting = true;
    // Submitted without a focus first (Safari doesn't focus a pressed button): start the captcha attaching,
    // without moving focus (that would raise the phone keyboard)
    if (!focused) this.refs.email.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));

    const started = Date.now();
    while (!form.dataset.hcaptchaBound && Date.now() - started < CHECK_ATTACH_WAIT) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    this.#waiting = false;
    if (!this.isConnected) return;
    if (form.dataset.hcaptchaBound) form.requestSubmit();
    else this.#send(form);
  }

  /**
   * While Shopify's check runs. Most shoppers pass it unseen. A puzzle sits on the page, under the bag (a modal
   * dialog on phones, a focus-trapped panel on desktop), where it can't be used: the bag closes while the puzzle is
   * up and reopens after (#settle). A puzzle closed unsolved, or a check that never answers (a blocked script),
   * ends the wait, so the form isn't left stuck.
   */
  #watchCheck() {
    if (this.#watching) return;
    this.#watching = true;
    const drawer = /** @type {Drawer | null} */ (this.closest('theme-drawer'));
    const started = Date.now();
    let puzzleSeen = false;
    let puzzleGoneAt = 0;

    const check = () => {
      if (!this.isConnected || this.#busy || this.getAttribute('data-state') !== 'sending') {
        this.#watching = false;
        return;
      }
      if (puzzleShown()) {
        puzzleSeen = true;
        puzzleGoneAt = 0;
        if (drawer?.isOpen) {
          this.#reopen = drawer;
          drawer.close?.();
        }
      } else if (puzzleSeen && !puzzleGoneAt) {
        puzzleGoneAt = Date.now();
      }

      const closedUnsolved = puzzleGoneAt > 0 && Date.now() - puzzleGoneAt > PUZZLE_CLOSED_GRACE;
      const timedOut = Date.now() - started > (puzzleSeen ? PUZZLE_TIMEOUT : CHECK_TIMEOUT);
      if (closedUnsolved || timedOut) {
        this.#watching = false;
        this.removeAttribute('data-state');
        // Closing the puzzle was the shopper's choice; a check that never answered is ours to report
        if (timedOut) this.#showError(true);
        this.#settle();
        return;
      }
      setTimeout(check, 250);
    };
    check();
  }

  /** @param {HTMLFormElement} form */
  async #send(form) {
    const restock = this.dataset.kind === 'restock';
    const variant = this.#sentVariant ?? this.dataset.variant;
    // A late captcha answer after the email was saved
    if (this.#busy || (restock ? this.#doneVariants.has(variant) : this.getAttribute('data-state') === 'done')) return;
    this.#busy = true;
    this.setAttribute('data-state', 'sending');
    this.#showError(false);
    this.refs.tags.value = restock ? restockTags(this.dataset.handle, variant) : await this.#bagTags();

    let outcome = 'error';
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      const url = new URL(response.url);
      if (url.pathname.includes('/challenge')) outcome = 'challenge';
      else if (response.ok && url.searchParams.get('customer_posted') === 'true') outcome = 'done';
    } catch {
      // Offline or blocked: reported below
    }
    this.#busy = false;

    if (outcome === 'challenge') {
      // Shopify wants a full captcha page: post the form normally so the shopper can complete it
      HTMLFormElement.prototype.submit.call(form);
      return;
    }
    if (outcome === 'done') {
      this.#retried = false;
      this.#done(variant);
    } else if (!this.#retried && form.dataset.hcaptchaBound) {
      // Sent before Shopify's check had attached (a slow connection): once more, through the check
      this.#retried = true;
      form.requestSubmit();
      return;
    } else {
      this.#retried = false;
      this.removeAttribute('data-state');
      this.#showError(true);
    }
    this.#settle();
  }

  /** @param {string | undefined} variant */
  #done(variant) {
    const { opener, done } = this.refs;
    if (this.dataset.kind === 'restock') {
      if (variant) this.#doneVariants.add(variant);
      // Shows the thanks, unless the shopper moved on to another size while it was sending
      this.removeAttribute('data-state');
      this.#update();
    } else {
      if (opener) opener.hidden = true;
      this.#showDone(true);
    }
    if (!done.hidden) done.focus({ preventScroll: true });
  }

  /** @param {boolean} done */
  #showDone(done) {
    this.refs.panel.hidden = done;
    this.refs.done.hidden = !done;
    if (done) this.setAttribute('data-state', 'done');
    else this.removeAttribute('data-state');
  }

  /** @param {boolean} show */
  #showError(show) {
    this.refs.error.hidden = !show;
  }

  #settle() {
    const drawer = this.#reopen;
    this.#reopen = null;
    if (!drawer) return;
    drawer.open?.();
    // The drawer focuses its first control on open; the shopper should land on the outcome instead
    const { done } = this.refs;
    if (!done.hidden) requestAnimationFrame(() => done.focus({ preventScroll: true }));
  }

  /** @returns {Promise<string>} the bag's tags from the cart as it is now, or as rendered when it can't be read */
  async #bagTags() {
    const rendered = this.refs.tags.value;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return rendered;
      const cart = await response.json();
      const bag = bagTag(cart.items ?? []);
      return bag ? `newsletter, saved-bag, ${bag}` : rendered;
    } catch {
      return rendered;
    }
  }
}

/**
 * The bag tag, as the snippet renders it: bag:<yymmddhhmmss UTC>:<variant>x<qty>.<variant>x<qty>…
 * The time lets the saved-bag email pick the newest of several saves. Lines past Shopify's tag limit are left out.
 * @param {{variant_id: number, quantity: number}[]} items
 */
function bagTag(items) {
  const stamp = new Date().toISOString().slice(2, 19).replace(/\D/g, '');
  let tag = '';
  for (const item of items) {
    const line = `${item.variant_id}x${item.quantity}`;
    const next = tag ? `${tag}.${line}` : `bag:${stamp}:${line}`;
    if (next.length > MAX_TAG_LENGTH) break;
    tag = next;
  }
  return tag;
}

/**
 * The restock tags, as the snippet renders them
 * @param {string | undefined} handle
 * @param {string | undefined} variant
 */
function restockTags(handle, variant) {
  const tags = ['newsletter', 'restock', `restock-${handle}`];
  if (variant) tags.push(`restock-v${variant}`);
  return tags.join(', ');
}

/** True while an hCaptcha puzzle is on screen outside the bag */
function puzzleShown() {
  return [...document.querySelectorAll('iframe[src*="hcaptcha"]')].some(
    (frame) =>
      !frame.closest('theme-drawer') &&
      frame.getBoundingClientRect().height > 0 &&
      // checkVisibility: Safari 17.4+; older browsers fall back to the frame's own visibility
      (frame.checkVisibility?.({ visibilityProperty: true, opacityProperty: true }) ??
        getComputedStyle(frame).visibility === 'visible')
  );
}

/**
 * True when no size can be bought in the selected colour.
 * @param {Element} root
 */
function noSizeLeft(root) {
  const sizes = root.querySelectorAll(`${SIZE_FIELDSET} input[data-option-available]`);
  return sizes.length > 0 && [...sizes].every((input) => input.getAttribute('data-option-available') === 'false');
}

if (!customElements.get('mlb-email-ask')) customElements.define('mlb-email-ask', MlbEmailAsk);
